import { useCallback, useEffect, useState } from 'react';
import type { OrganizationSummary } from 'minka-shared';
import organizationAPI from '@/services/organization-api';

export interface UseOrganizationsReturn {
  /** Organizations the user may create an island in; empty is a valid answer. */
  organizations: OrganizationSummary[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * The organizations behind the Create Island dropdown.
 *
 * A user with no admin island anywhere gets an empty list and names a new
 * organization instead, so an empty result is a state the form renders, not an
 * error to report.
 *
 * @param enabled - fetch only while the form that needs the list is open
 */
export function useOrganizations(enabled: boolean = true): UseOrganizationsReturn {
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      setOrganizations(await organizationAPI.getMyOrganizations());
    } catch (loadError) {
      setOrganizations([]);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const result = await organizationAPI.getMyOrganizations();
        if (!cancelled) {
          setOrganizations(result);
        }
      } catch (loadError) {
        if (!cancelled) {
          setOrganizations([]);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load organizations');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    // A late answer must not land on a closed form.
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { organizations, isLoading, error, reload };
}
